import React, { useEffect, useMemo, useState } from 'react';
import { opsApi, type DataFix, type DataFixRun, type FixPreview, type FixRunResult } from '../../../api/dbOps';
import { REGISTRY_TARGET, errorText } from '../../../api/migrationRunner';
import { Alert, Btn, Dialog, ErrorBox, Kpi, Panel, Pill, Spinner, when } from '../runner/ui';
import { Empty, td, th, usePoll } from './shared';

/**
 * Data fixes (approved mockup "Database redesign", Data fixes): backfills
 * and cleanups that show what they'll change, save exactly those rows first,
 * and can be undone for 30 days. Files live in CITS-Server data-fixes/ (or a
 * migration folder with `-- @kind: data-fix`).
 */
const RUN_TONE: Record<DataFixRun['status'], 'ok' | 'bad' | 'warn' | 'mut' | 'info'> = {
  running: 'info', success: 'ok', failed: 'bad', undone: 'mut', undo_failed: 'bad',
};

const DataFixesTab: React.FC<{ isOwner: boolean }> = ({ isOwner }) => {
  const list = usePoll(() => opsApi.dataFixes(), null);
  const servers = usePoll(() => opsApi.servers(), null);
  const [path, setPath] = useState<string | null>(null);
  const fix = useMemo(() => list.data?.fixes.find((f) => f.path === path) || list.data?.fixes[0] || null, [list.data, path]);

  return (
    <div className="space-y-4">
      <p className="text-[13.5px] text-slate-500">
        Backfills and cleanups are their own kind: they show what they'll change before running, save just those rows first, and can be undone for 30 days.
      </p>
      {list.data && !list.data.schema_ready && (
        <Alert tone="warn" title="Running fixes needs the registry migration migrations-2026-10-11-db-ops">Preview works now; Run and Undo need its tables.</Alert>
      )}
      {list.error ? <ErrorBox>{errorText(list.error)}</ErrorBox> : null}
      {list.loading && !list.data && <Spinner />}
      {list.data && !list.data.fixes.length && (
        <Empty>No data fixes yet. Add a .sql file to CITS-Server <code>data-fixes/</code> (see its README) and deploy.</Empty>
      )}
      {list.data && list.data.fixes.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <div className="space-y-1.5">
            {list.data.fixes.map((f) => (
              <button
                key={f.path}
                type="button"
                onClick={() => setPath(f.path)}
                className={`block w-full rounded-lg border px-3 py-2 text-left text-[13px] ${fix?.path === f.path ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              >
                <span className="block font-semibold text-slate-900">{f.title}</span>
                <span className="block truncate text-[11.5px] text-slate-500">{f.path}</span>
                {f.errors.length > 0 && <Pill tone="bad">{f.errors.length} problem{f.errors.length === 1 ? '' : 's'}</Pill>}
              </button>
            ))}
          </div>
          {fix && (
            <FixPanel
              key={fix.path}
              fix={fix}
              isOwner={isOwner}
              schemaReady={list.data.schema_ready}
              companies={(servers.data || []).flatMap((s) => s.companies)}
              onChanged={() => void list.reload()}
            />
          )}
        </div>
      )}
    </div>
  );
};

const FixPanel: React.FC<{
  fix: DataFix;
  isOwner: boolean;
  schemaReady: boolean;
  companies: { id: string; name: string; database: string }[];
  onChanged: () => void;
}> = ({ fix, isOwner, schemaReady, companies, onChanged }) => {
  const options = useMemo(() => (fix.scope === 'registry'
    ? [{ value: REGISTRY_TARGET, label: 'Registry' }]
    : companies.map((c) => ({ value: c.database, label: c.name })).sort((a, b) => a.label.localeCompare(b.label))), [fix.scope, companies]);
  const [targets, setTargets] = useState<string[]>([]);
  useEffect(() => { setTargets(options.map((o) => o.value)); }, [options]);
  const [preview, setPreview] = useState<FixPreview | null>(null);
  const [busy, setBusy] = useState<'preview' | 'run' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<FixRunResult | null>(null);
  const [undo, setUndo] = useState<DataFixRun | null>(null);
  const [undoMsg, setUndoMsg] = useState<string | null>(null);

  const toggle = (v: string) => { setPreview(null); setTargets((t) => (t.includes(v) ? t.filter((x) => x !== v) : [...t, v])); };
  const total = preview?.results.reduce((s, r) => s + (r.rows || 0), 0) || 0;
  const withRows = preview?.results.filter((r) => (r.rows || 0) > 0).map((r) => r.target) || [];

  const doPreview = async () => {
    setBusy('preview'); setErr(null); setResult(null);
    try { setPreview(await opsApi.previewFix(fix.path, targets)); } catch (e) { setErr(errorText(e)); } finally { setBusy(null); }
  };
  const doRun = async () => {
    if (!preview) return;
    setBusy('run'); setErr(null);
    try {
      setResult(await opsApi.runFix(fix.path, withRows, preview.fix.hash));
      setConfirm(false);
      setPreview(null);
      onChanged();
    } catch (e) { setErr(errorText(e)); setConfirm(false); } finally { setBusy(null); }
  };
  const doUndo = async () => {
    if (!undo) return;
    try {
      const r = await opsApi.undoFix(undo.id);
      setUndoMsg(`Put back ${r.restored} rows on ${undo.target_db}.${r.dropped_columns.length ? ` Columns no longer in the table were skipped: ${r.dropped_columns.join(', ')}.` : ''}`);
      setUndo(null);
      onChanged();
    } catch (e) { setUndoMsg(errorText(e)); setUndo(null); }
  };

  return (
    <div className="space-y-4">
      <Panel title={fix.title} actions={<><Pill tone="vio">data fix</Pill><Pill tone="mut">{fix.scope}</Pill></>}>
        <div className="space-y-3">
          {fix.errors.length > 0 && <Alert tone="bad" title="This file can't run until these are fixed">{fix.errors.map((e) => <span key={e} className="block">{e}</span>)}</Alert>}
          <div className="text-[12.5px] text-slate-500">Changes <code>{fix.table}</code> (key <code>{fix.pk}</code>). Rows it changes:</div>
          <pre className="overflow-auto whitespace-pre-wrap rounded-lg bg-slate-100 p-3 font-mono text-[12px]">{fix.preview}</pre>
          <pre className="overflow-auto whitespace-pre-wrap rounded-lg border border-red-200 bg-red-50 p-3 font-mono text-[12px] text-red-900">{fix.statements.join(';\n\n')};</pre>
          {fix.scope === 'tenant' && (
            <details className="text-[13px]">
              <summary className="cursor-pointer font-semibold text-slate-700">{targets.length} of {options.length} companies</summary>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <button type="button" className="text-blue-600" onClick={() => { setPreview(null); setTargets(options.map((o) => o.value)); }}>All</button>
                <button type="button" className="text-blue-600" onClick={() => { setPreview(null); setTargets([]); }}>None</button>
                {options.map((o) => (
                  <label key={o.value} className="flex items-center gap-1"><input type="checkbox" checked={targets.includes(o.value)} onChange={() => toggle(o.value)} />{o.label}</label>
                ))}
              </div>
            </details>
          )}
          {preview && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Kpi label="Will change" value={`${total.toLocaleString()} rows`} />
                <Kpi label="Companies" value={withRows.length} />
                <Kpi label="Snapshot first" value={`${total.toLocaleString()} rows saved`} />
              </div>
              {preview.results.filter((r) => !r.ok || r.too_many).map((r) => (
                <Alert key={r.target} tone="bad" title={r.target}>{r.too_many ? 'More than 20,000 rows: split the fix.' : r.error}</Alert>
              ))}
              <details className="text-[12.5px]">
                <summary className="cursor-pointer text-slate-600">Rows per database</summary>
                <table className="mt-2 w-full"><tbody>
                  {preview.results.map((r) => (
                    <tr key={r.target}><td className={td}>{r.target}</td><td className={`${td} tabular-nums`}>{r.ok ? r.rows : '—'}</td><td className={`${td} font-mono text-[11.5px] text-slate-500`}>{(r.sample_keys || []).slice(0, 8).join(', ')}</td></tr>
                  ))}
                </tbody></table>
              </details>
            </>
          )}
          {err && <ErrorBox>{err}</ErrorBox>}
          <div className="flex flex-wrap items-center gap-2">
            <Btn kind="ghost" disabled={!targets.length || busy !== null || fix.errors.length > 0} onClick={doPreview}>{busy === 'preview' && <Spinner />}Preview rows</Btn>
            {isOwner && (
              <Btn disabled={!preview || !total || busy !== null || !schemaReady} onClick={() => setConfirm(true)}>Run</Btn>
            )}
            <span className="text-[12.5px] text-slate-500">Undo available for 30 days</span>
          </div>
        </div>
      </Panel>

      {result && (
        <Alert tone={result.results.every((r) => r.status === 'success') ? 'ok' : 'bad'} title={`Ran on ${result.results.length} database${result.results.length === 1 ? '' : 's'}${result.skipped ? `, ${result.skipped} not run (stopped after a failure)` : ''}`}>
          {result.results.map((r) => (
            <span key={r.run_id} className="block">{r.target}: {r.status === 'success' ? `${r.rows_changed} rows changed` : `${r.code ? `${r.code}: ` : ''}${r.error}`}</span>
          ))}
        </Alert>
      )}
      {undoMsg && <Alert tone="info" title={undoMsg} action={<Btn kind="ghost" small onClick={() => setUndoMsg(null)}>Dismiss</Btn>} />}

      <Panel title="Runs" bodyClass="p-0">
        {fix.runs.length === 0 ? <div className="p-4 text-[13px] text-slate-500">Not run yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50"><tr><th className={th}>When</th><th className={th}>Database</th><th className={th}>Rows</th><th className={th}>By</th><th className={th}>Status</th><th className={th} /></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {fix.runs.map((r) => {
                  const canUndo = r.status === 'success' && r.undo_until && new Date(r.undo_until).getTime() > Date.now();
                  return (
                    <tr key={r.id}>
                      <td className={`${td} whitespace-nowrap`}>{when(r.started_at)}</td>
                      <td className={td}>{r.target_db}</td>
                      <td className={`${td} tabular-nums`}>{r.rows_changed ?? r.rows_matched}</td>
                      <td className={td}>{r.executed_by}</td>
                      <td className={td}>
                        <Pill tone={RUN_TONE[r.status]}>{r.status === 'undone' ? `undone by ${r.undone_by}` : r.status}</Pill>
                        {r.error_message && <div className="mt-1 max-w-sm text-[11.5px] text-red-700">{r.error_message}</div>}
                      </td>
                      <td className={`${td} text-right`}>
                        {isOwner && canUndo && <Btn kind="ghost" small onClick={() => setUndo(r)}>Undo</Btn>}
                        {canUndo && <div className="text-[11px] text-slate-400">until {when(r.undo_until)}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Dialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title={`Run "${fix.title}"?`}
        footer={<><Btn kind="ghost" onClick={() => setConfirm(false)}>Cancel</Btn><Btn kind="danger" disabled={busy === 'run'} onClick={doRun}>{busy === 'run' && <Spinner />}Run on {withRows.length} database{withRows.length === 1 ? '' : 's'}</Btn></>}
      >
        <p>Changes {total.toLocaleString()} rows in <code>{fix.table}</code>. Each database runs in one transaction under the migration lock: the rows are saved first, and if the fix changes more rows than the preview listed nothing is kept.</p>
        <p className="text-[12.5px] text-slate-500">Needs a fresh sign-in. Stops at the first failing database.</p>
      </Dialog>
      <Dialog
        open={!!undo}
        onClose={() => setUndo(null)}
        title="Undo this run?"
        footer={<><Btn kind="ghost" onClick={() => setUndo(null)}>Cancel</Btn><Btn kind="danger" onClick={doUndo}>Put the rows back</Btn></>}
      >
        {undo && <p>Puts the {undo.rows_snapshotted} saved rows on <b>{undo.target_db}</b> back as they were on {when(undo.started_at)}. Changes made to those rows since then are overwritten.</p>}
      </Dialog>
    </div>
  );
};

export default DataFixesTab;
